import { Link } from "react-router-dom";
import { ArrowRight, Check, Sparkles } from "lucide-react";
import PageHero from "../components/PageHero";
import Reveal from "../components/Reveal";
import { useBooking } from "../lib/booking";
import { IMAGES, MODALITIES, PRICING, FOUNDERS } from "../data/content";

const STEPS = [
  { n: "01", title: "A free hello", text: "Twenty minutes, no cost, no commitment. You talk, we listen, we see if we fit." },
  { n: "02", title: "A path, drawn together", text: "Together we sketch what the work could look like — gentle goals, honest pace, no pressure." },
  { n: "03", title: "The walk itself", text: "Weekly or fortnightly sessions. We check in often, and you can pause anytime." },
];

export default function IndividualHealing() {
  const { openBooking } = useBooking();

  return (
    <div>
      <PageHero
        eyebrow="Individual Healing"
        lines={["One room,", "two chairs,", "zero judgement."]}
        sub="One-on-one online sessions for anxiety, burnout, self-worth and the quiet stuff in between. You lead. We walk beside you."
        image={IMAGES.therapyHands}
        alt="A warm, supportive therapy conversation"
      >
        <button
          data-testid="healing-hero-book-button"
          onClick={() => openBooking()}
          className="group flex items-center gap-2 rounded-full bg-pine px-7 py-3.5 text-sm font-semibold text-cream transition-all duration-300 hover:-translate-y-0.5 hover:bg-pinedeep hover:shadow-xl"
        >
          Book a free intro call
          <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5" />
        </button>
      </PageHero>

      {/* How it works */}
      <section className="mx-auto max-w-7xl px-5 py-24 sm:px-8">
        <Reveal>
          <p className="eyebrow">How it works</p>
          <h2 className="mt-4 font-serif text-4xl tracking-tight text-ink sm:text-5xl">
            Three gentle steps. That's the whole process.
          </h2>
        </Reveal>
        <div className="mt-12 grid gap-5 md:grid-cols-3">
          {STEPS.map((s, i) => (
            <Reveal key={s.n} delay={i * 0.09}>
              <div className="card-soft h-full p-8" data-testid={`healing-step-${i}`}>
                <p className="font-mono text-sm tracking-[0.2em] text-terra">{s.n}</p>
                <h3 className="mt-4 font-serif text-2xl text-ink">{s.title}</h3>
                <p className="mt-3 text-sm leading-relaxed text-bodytext">{s.text}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* Modalities */}
      <section className="bg-creamdeep py-24">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <Reveal>
            <p className="eyebrow">What we draw on</p>
            <h2 className="mt-4 font-serif text-4xl tracking-tight text-ink sm:text-5xl">
              Four ways in. One warm room.
            </h2>
          </Reveal>
          <div className="mt-12 grid gap-5 sm:grid-cols-2">
            {MODALITIES.map((m, i) => (
              <Reveal key={m.title} delay={i * 0.07}>
                <div className="flex h-full gap-5 rounded-3xl border border-pine/10 bg-white/85 p-7" data-testid={`healing-modality-${i}`}>
                  <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-sagelight text-pine">
                    <Sparkles className="h-5 w-5" />
                  </span>
                  <div>
                    <h3 className="font-serif text-xl text-ink">{m.title}</h3>
                    <p className="mt-2 text-sm leading-relaxed text-bodytext">{m.text}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section className="mx-auto max-w-7xl px-5 py-24 sm:px-8">
        <Reveal className="text-center">
          <p className="eyebrow">Session pricing</p>
          <h2 className="mt-4 font-serif text-4xl tracking-tight text-ink sm:text-5xl">
            Clear prices. Sliding-scale spots. Always.
          </h2>
        </Reveal>
        <div className="mt-14 grid gap-6 md:grid-cols-3">
          {PRICING.map((p, i) => (
            <Reveal key={p.name} delay={i * 0.09}>
              <div
                data-testid={`healing-pricing-${i}`}
                className={`flex h-full flex-col rounded-[2rem] p-8 transition-all duration-300 hover:-translate-y-1 ${
                  p.featured
                    ? "bg-pine text-cream shadow-[0_24px_60px_rgba(45,90,70,0.3)]"
                    : "border border-pine/10 bg-white/85"
                }`}
              >
                <p className={`font-mono text-[11px] uppercase tracking-[0.22em] ${p.featured ? "text-blush" : "text-sage"}`}>
                  {p.meta}
                </p>
                <h3 className="mt-3 font-serif text-2xl">{p.name}</h3>
                <p className="mt-4 font-serif text-5xl">{p.price}</p>
                <ul className="mt-6 flex-1 space-y-3">
                  {p.features.map((f) => (
                    <li key={f} className="flex items-start gap-2.5 text-sm">
                      <Check className={`mt-0.5 h-4 w-4 shrink-0 ${p.featured ? "text-blush" : "text-sage"}`} />
                      {f}
                    </li>
                  ))}
                </ul>
                <button
                  data-testid={`healing-pricing-cta-${i}`}
                  onClick={() => openBooking(p.featured ? "session" : "intro")}
                  className={`mt-8 rounded-full py-3.5 text-sm font-semibold transition-all duration-300 hover:-translate-y-0.5 ${
                    p.featured
                      ? "bg-cream text-pinedeep hover:bg-blush"
                      : "bg-pine text-cream hover:bg-pinedeep"
                  }`}
                >
                  Book this
                </button>
              </div>
            </Reveal>
          ))}
        </div>
        <p className="mt-6 text-center text-xs italic text-bodytext/60">
          All prices in USD. A limited number of sliding-scale seats is held every month — simply ask.
        </p>
      </section>

      {/* Who you meet */}
      <section className="border-t border-pine/10 bg-sagelight/40 py-24">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <Reveal>
            <p className="eyebrow">Who you'll meet</p>
            <h2 className="mt-4 font-serif text-4xl tracking-tight text-ink sm:text-5xl">
              Two temperatures. One safe room.
            </h2>
          </Reveal>
          <div className="mt-12 grid gap-6 md:grid-cols-2">
            {FOUNDERS.map((f, i) => (
              <Reveal key={f.persona} delay={i * 0.1}>
                <div className="flex h-full flex-col gap-6 rounded-[2rem] bg-white/85 p-8 sm:flex-row sm:items-start" data-testid={`healing-founder-${i}`}>
                  <img
                    src={IMAGES.therapistPortrait}
                    alt={f.persona}
                    loading="lazy"
                    className="h-40 w-40 shrink-0 rounded-3xl object-cover"
                  />
                  <div>
                    <h3 className="font-serif text-2xl italic text-pine">{f.persona}</h3>
                    <p className="mt-2 font-mono text-[11px] uppercase tracking-[0.2em] text-sage">
                      {f.creds}
                    </p>
                    <p className="mt-4 text-sm leading-relaxed text-bodytext">{f.text}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
          <p className="mt-8 text-center text-sm italic text-bodytext/70">
            Back of the Mind offers coaching and NLP-based support, not licensed clinical
            care — when clinical help fits better, we refer you warmly.
          </p>
          <Reveal className="mt-10 text-center">
            <Link
              data-testid="healing-faq-link"
              to="/faqs"
              className="text-sm font-semibold text-pine underline underline-offset-4 hover:text-pinedeep"
            >
              Questions about confidentiality, insurance or tech? Read the FAQs
            </Link>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
